import React, { useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Loader2, Play, Pause, ChevronRight, Search } from 'lucide-react';
// --- SHARED DATA ---
type UseCase = {
  id: string;
  category: string;
  role: string;
  industry: string;
  tool: {
    slug: string;
    name: string;
    light?: boolean;
  };
  title: string;
  result: string;
};
const USE_CASES: UseCase[] = [{
  id: 'uc-1',
  category: 'Ops',
  role: 'Founder',
  industry: 'E-commerce',
  tool: {
    slug: 'stripe',
    name: 'Stripe'
  },
  title: 'Weekly revenue pulse',
  result: 'Closed last week’s books and posted the $82k recap — up 14%'
}, {
  id: 'uc-2',
  category: 'Support',
  role: 'Support manager',
  industry: 'SaaS',
  tool: {
    slug: 'zendesk',
    name: 'Zendesk',
    light: true
  },
  title: 'Ticket triage',
  result: 'Triaged 42 overnight tickets and queued replies before standup'
}, {
  id: 'uc-3',
  category: 'Sales',
  role: 'Sales lead',
  industry: 'Agency',
  tool: {
    slug: 'salesforce',
    name: 'Salesforce'
  },
  title: 'Meeting follow-ups',
  result: 'Sent follow-ups for today’s 4 calls and updated every deal'
}, {
  id: 'uc-4',
  category: 'Ops',
  role: 'Founder',
  industry: 'E-commerce',
  tool: {
    slug: 'shopify',
    name: 'Shopify'
  },
  title: 'Inventory reorder',
  result: 'Caught 12 products running low and placed the reorder'
}, {
  id: 'uc-5',
  category: 'Marketing',
  role: 'Marketer',
  industry: 'Agency',
  tool: {
    slug: 'hubspot',
    name: 'HubSpot'
  },
  title: 'Campaign performance',
  result: 'Tracked summer-campaign ROI across every channel and flagged the winner'
}, {
  id: 'uc-6',
  category: 'Retention',
  role: 'Success lead',
  industry: 'SaaS',
  tool: {
    slug: 'hubspot',
    name: 'HubSpot'
  },
  title: 'Renewal watch',
  result: 'Flagged 7 accounts up for renewal and nudged each owner'
}];
// --- SHARED COMPONENTS ---
function ToolIcon({
  slug,
  light,
  size = 'md'
}: {
  slug: string;
  light?: boolean;
  size?: 'sm' | 'md' | 'lg';
}) {
  const dims = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-8 h-8' : 'w-5 h-5';
  const containerDims = size === 'sm' ? 'w-6 h-6 rounded-md' : size === 'lg' ? 'w-14 h-14 rounded-xl' : 'w-8 h-8 rounded-lg';
  return <div className={`flex items-center justify-center bg-[#FAF8F4] border border-neutral-200/60 shrink-0 ${containerDims}`} data-id="element-12893" data-mp="1bsi8dfy22fe9">
      <img src={`https://cdn.simpleicons.org/${slug}${light ? '/2B211A' : ''}`} alt="" className={`${dims} object-contain`} loading="lazy" onError={e => {
      const img = e.currentTarget as HTMLImageElement;
      img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;
    }} data-id="element-12894" data-mp="f30uwestzp20" />
    </div>;
}
// --- CONCEPT 1: STICKER WALL ---
function ConceptStickerWall() {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [filter, setFilter] = useState('All');
  const categories = ['All', ...Array.from(new Set(USE_CASES.map(u => u.category)))];
  const filtered = filter === 'All' ? USE_CASES : USE_CASES.filter(u => u.category === filter);
  return <div className="bg-[#FAF8F4] rounded-3xl p-8 md:p-12 border border-neutral-200/60" data-id="element-12895" data-mp="29maryto3fccz">
      <div className="mb-8 flex flex-wrap gap-2 justify-center" data-id="element-12896" data-mp="24vavtqmssybj">
        {categories.map(c => <button key={c} onClick={() => {
        setFilter(c);
        setActiveId(null);
      }} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${filter === c ? 'bg-[#2B211A] text-white' : 'bg-white text-neutral-500 hover:text-[#2B211A] border border-neutral-200'}`} data-id="element-12897" data-mp="24sy80wucztqk">
            {c}
          </button>)}
      </div>

      <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto" data-id="element-12898" data-mp="1lxhugsrtowg9">
        <AnimatePresence mode="popLayout" data-id="element-12899" data-mp="1hvd1lw9783uo">
          {filtered.map(uc => {
          const isActive = activeId === uc.id;
          return <motion.div layout initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.8
          }} key={uc.id} onClick={() => setActiveId(isActive ? null : uc.id)} className={`relative cursor-pointer bg-white border border-neutral-200/80 shadow-sm hover:shadow-md transition-shadow rounded-2xl overflow-hidden ${isActive ? 'w-full max-w-md' : 'w-auto'}`} data-id="element-12900" data-mp="3g96gfewh1za5">
                <div className="flex items-center gap-3 p-4" data-id="element-12901" data-mp="3ebofqujdroyr">
                  <ToolIcon slug={uc.tool.slug} light={uc.tool.light} data-id="element-12902" data-mp="30l2wv5txff0v" />
                  <span className="font-semibold text-[#2B211A] whitespace-nowrap" data-id="element-12903" data-mp="30jrrj1ku1z7n">
                    {uc.title}
                  </span>
                </div>

                <AnimatePresence data-id="element-12904" data-mp="2qoq1m3iqgt40">
                  {isActive && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="px-4 pb-4" data-id="element-12905" data-mp="2vmhxf8mi0zdh">
                      <div className="flex items-start gap-3 pt-3 border-t border-neutral-100" data-id="element-12906" data-mp="22zrxfsfw9gs5">
                        <div className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-[#EA580C] text-white shrink-0" data-id="element-12907" data-mp="1y0x7m6jieryj">
                          <Check size={12} strokeWidth={3} data-id="element-12908" data-mp="2c1rcif7cmasg" />
                        </div>
                        <span className="text-sm font-medium text-[#2B211A] leading-snug" data-id="element-12909" data-mp="27yv0j7cbdcxh">
                          {uc.result}
                        </span>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </AnimatePresence>
      </div>
    </div>;
}
// --- CONCEPT 2: FILTERABLE CARD GRID ---
function ConceptCardGrid() {
  const [filter, setFilter] = useState('All');
  const categories = ['All', ...Array.from(new Set(USE_CASES.map(u => u.category)))];
  const filtered = filter === 'All' ? USE_CASES : USE_CASES.filter(u => u.category === filter);
  return <div className="bg-white rounded-3xl p-8 md:p-12 border border-neutral-200/60" data-id="element-12910" data-mp="2zs3smdcqzdu4">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-8" data-id="element-12911" data-mp="1xeftwdmvhkh9">
        <div className="flex gap-2" data-id="element-12912" data-mp="22cr2ym5cfqyf">
          {categories.map(c => <button key={c} onClick={() => setFilter(c)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === c ? 'bg-[#EA580C]/10 text-[#EA580C]' : 'text-neutral-500 hover:bg-neutral-100'}`} data-id="element-12913" data-mp="26dt272z9g30e">
              {c}
            </button>)}
        </div>
        <div className="relative" data-id="element-12914" data-mp="2g5qdsau6kwzn">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-12915" data-mp="2l1ny0s8kenhp" />
          <input type="text" placeholder="Search use cases..." className="pl-9 pr-4 py-2 rounded-xl border border-neutral-200 text-sm focus:outline-none focus:border-[#EA580C]/50" data-id="element-12916" data-mp="2q1abn2sb25r1" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-12917" data-mp="vokgkhq7aqi2">
        <AnimatePresence mode="popLayout" data-id="element-12918" data-mp="zoksnvptuply">
          {filtered.map(uc => <motion.div layout initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} key={uc.id} className="group flex flex-col bg-[#FAF8F4] border border-neutral-200/80 rounded-2xl p-6 hover:border-[#EA580C]/30 transition-colors" data-id="element-12919" data-mp="zr7260wa3f6t">
              <div className="flex items-center gap-3 mb-4" data-id="element-12920" data-mp="ef86e24c09k4">
                <ToolIcon slug={uc.tool.slug} light={uc.tool.light} data-id="element-12921" data-mp="1b47d3cp9p3a7" />
                <h3 className="font-bold text-[#2B211A]" data-id="element-12922" data-mp="1b2d1wq3sspjb">{uc.title}</h3>
              </div>
              <div className="mt-auto" data-id="element-12923" data-mp="1665suog3uxx5">
                <div className="flex items-start gap-3 pt-4 border-t border-neutral-200/60" data-id="element-12924" data-mp="123iyogvyp0ws">
                  <div className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-[#EA580C] text-white shrink-0" data-id="element-12925" data-mp="x4y8laafbxm2">
                    <Check size={12} strokeWidth={3} data-id="element-12926" data-mp="3mv1j05vjl0vw" />
                  </div>
                  <span className="text-sm font-medium text-[#2B211A] leading-snug" data-id="element-12927" data-mp="3hudvvvafp46y">
                    {uc.result}
                  </span>
                </div>
              </div>
            </motion.div>)}
        </AnimatePresence>
      </div>
    </div>;
}
// --- CONCEPT 3: INDUSTRY SWITCHER ---
function ConceptIndustrySwitcher() {
  const industries = Array.from(new Set(USE_CASES.map(u => u.industry)));
  const [activeIndustry, setActiveIndustry] = useState(industries[0]);
  const filtered = USE_CASES.filter(u => u.industry === activeIndustry);
  return <div className="bg-[#2B211A] rounded-3xl p-8 md:p-16 text-white overflow-hidden" data-id="element-12928" data-mp="24j1vi4iu1z20">
      <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-12929" data-mp="29gtq56av3z9w">
        <h2 className="text-3xl md:text-5xl font-title font-medium mb-6" data-id="element-12930" data-mp="1usywnnp15qvr">
          What can Giga do for me?
        </h2>
        <div className="inline-flex flex-wrap items-center justify-center gap-3 text-xl md:text-2xl text-neutral-400" data-id="element-12931" data-mp="1zq8thoer14tm">
          <span data-id="element-12932" data-mp="2nftd89iq863y">I work in</span>
          <div className="relative inline-block" data-id="element-12933" data-mp="2s7un5osf9kqt">
            <select value={activeIndustry} onChange={e => setActiveIndustry(e.target.value)} className="appearance-none bg-white/10 border border-white/20 rounded-xl px-4 py-2 pr-10 text-white font-semibold focus:outline-none focus:ring-2 focus:ring-[#EA580C]" data-id="element-12934" data-mp="2s8mhkwaa9cfi">
              {industries.map(i => <option key={i} value={i} className="text-black" data-id="element-12935" data-mp="pk4dz5o33ejq">
                  {i}
                </option>)}
            </select>
            <ChevronRight size={20} className="absolute right-3 top-1/2 -translate-y-1/2 rotate-90 pointer-events-none" data-id="element-12936" data-mp="lkdqpbxrngjy" />
          </div>
        </div>
        <p className="mt-5 text-sm text-neutral-500" data-id="element-12937" data-mp="3p0u929vrzyj4">
          Pre-filled from the industry you pick during onboarding — change it
          anytime.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-12938" data-mp="1vl5ap14gzr4">
        <AnimatePresence mode="wait" data-id="element-12939" data-mp="1xplo6g7jr8a">
          {filtered.map(uc => <motion.div key={uc.id + activeIndustry} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm" data-id="element-12940" data-mp="1i8tkjk7wsc2v">
              <div className="flex items-center gap-3 mb-4" data-id="element-12941" data-mp="3duqrrrfvih6x">
                <ToolIcon slug={uc.tool.slug} light={uc.tool.light} data-id="element-12942" data-mp="392h46i28dt7t" />
                <h3 className="font-bold text-white" data-id="element-12943" data-mp="390csvf205rxi">{uc.title}</h3>
              </div>
              <div className="flex items-start gap-3 mt-4 pt-4 border-t border-white/10" data-id="element-12944" data-mp="350vpcr64z4uz">
                <div className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-[#EA580C] text-white shrink-0" data-id="element-12945" data-mp="303c95lpvoi0e">
                  <Check size={12} strokeWidth={3} data-id="element-12946" data-mp="2q3v518q9yc6j" />
                </div>
                <span className="text-sm font-medium text-white leading-snug" data-id="element-12947" data-mp="2q44vexf8elcr">
                  {uc.result}
                </span>
              </div>
            </motion.div>)}
        </AnimatePresence>
      </div>
    </div>;
}
// --- CONCEPT 4: ANIMATED FEED ---
function ConceptAnimatedFeed() {
  const [isPlaying, setIsPlaying] = useState(true);
  const [visibleCount, setVisibleCount] = useState(1);
  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      setVisibleCount(prev => prev < USE_CASES.length ? prev + 1 : 1);
    }, 3000);
    return () => clearInterval(interval);
  }, [isPlaying]);
  return <div className="bg-[#FAF8F4] rounded-3xl p-8 md:p-12 border border-neutral-200/60 flex flex-col md:flex-row gap-12 items-start" data-id="element-12948" data-mp="2fzogq7dhnaaj">
      <div className="flex-1 sticky top-12" data-id="element-12949" data-mp="1jaz00kdlknq5">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EA580C]/10 text-[#EA580C] text-xs font-bold uppercase tracking-widest mb-6" data-id="element-12950" data-mp="1jc0c8s6cng9g">
          <span className="relative flex h-2 w-2" data-id="element-12951" data-mp="1s9kb5ugqp0gw">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EA580C] opacity-75" data-id="element-12952" data-mp="1x96sog3lp2vi"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#EA580C]" data-id="element-12953" data-mp="33qabwy2e9034"></span>
          </span>
          Live Activity
        </div>
        <h2 className="text-3xl md:text-4xl font-title font-medium text-[#2B211A] mb-4" data-id="element-12954" data-mp="38qongq37es5w">
          Watch Giga work.
        </h2>
        <p className="text-neutral-500 mb-8 max-w-sm" data-id="element-12955" data-mp="14l0vw7com17e">
          A simulated feed of real tasks Giga is completing right now across
          different roles and tools.
        </p>
        <button onClick={() => setIsPlaying(!isPlaying)} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-neutral-200 text-sm font-bold text-[#2B211A] hover:bg-neutral-50 transition-colors" data-id="element-12956" data-mp="1ii5zm0oxanzh">
          {isPlaying ? <>
              <Pause size={16} data-id="element-12957" data-mp="ky2w70z4vgjp" /> Pause Feed
            </> : <>
              <Play size={16} data-id="element-12958" data-mp="zle8x671x6le" /> Resume Feed
            </>}
        </button>
      </div>

      <div className="flex-1 w-full max-w-md flex flex-col gap-4" data-id="element-12959" data-mp="uuxsrebuj6vc">
        <AnimatePresence initial={false} data-id="element-12960" data-mp="utmprgq8635a">
          {USE_CASES.slice(0, visibleCount).reverse().map((uc, index) => {
          const isDone = index > 0;
          return <motion.div key={uc.id} initial={{
            opacity: 0,
            y: -20,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} className="bg-white border border-neutral-200/80 rounded-2xl p-5 shadow-sm" data-id="element-12961" data-mp="271zykpedz3u" data-mpi={index}>
                  <div className="flex items-center justify-between mb-3" data-id="element-12962" data-mp="24tqi3pwbr87k">
                    <span className="text-[11px] font-bold text-neutral-400 uppercase tracking-wider" data-id="element-12963" data-mp="28uivmvc8bz9n">
                      {uc.category}
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-12964" data-mp="2wkwlcf7cdm12">Just now</span>
                  </div>
                  <div className="flex items-center gap-3 mb-1" data-id="element-12965" data-mp="31iex0dcqufcr">
                    <ToolIcon slug={uc.tool.slug} light={uc.tool.light} size="sm" data-id="element-12966" data-mp="2nidnk9iexzlo" />
                    <h3 className="text-sm font-bold text-[#2B211A]" data-id="element-12967" data-mp="2si08740kmi4i">
                      {uc.title}
                    </h3>
                  </div>

                  <div className="mt-3" data-id="element-12968" data-mp="1c6csydyvbgy0">
                    {isDone ? <div className="flex items-start gap-3 pt-3 border-t border-neutral-100" data-id="element-12969" data-mp="1h3mqkgweuywx">
                        <div className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-[#EA580C] text-white shrink-0" data-id="element-12970" data-mp="1wd9nm5u9jljz">
                          <Check size={12} strokeWidth={3} data-id="element-12971" data-mp="1rkyo343fq1ry" />
                        </div>
                        <span className="text-sm font-medium text-[#2B211A] leading-snug" data-id="element-12972" data-mp="1mnq3kwutdlnj">
                          {uc.result}
                        </span>
                      </div> : <div className="flex items-center gap-3 pt-3 border-t border-neutral-100 opacity-60" data-id="element-12973" data-mp="2fc43ji5gqs1q">
                        <Loader2 size={16} className="text-[#EA580C] animate-spin shrink-0" data-id="element-12974" data-mp="2adu7l5bwznf2" />
                        <span className="text-sm text-neutral-500 italic" data-id="element-12975" data-mp="26aebqb7o5r3f">
                          Working...
                        </span>
                      </div>}
                  </div>
                </motion.div>;
        })}
        </AnimatePresence>
      </div>
    </div>;
}
// --- CONCEPT 5: STACKED CARD SHUFFLE ---
function ConceptStackedShuffle() {
  const [cards, setCards] = useState(USE_CASES);
  const handleNext = () => {
    setCards(prev => {
      const newCards = [...prev];
      const first = newCards.shift();
      if (first) newCards.push(first);
      return newCards;
    });
  };
  return <div className="bg-[#1A1410] rounded-3xl p-8 md:p-20 overflow-hidden flex flex-col items-center justify-center min-h-[600px]" data-id="element-12976" data-mp="3ajwktpt3x1ic">
      <div className="text-center mb-12" data-id="element-12977" data-mp="3fcz7ddzgvap1">
        <h2 className="text-3xl font-title font-medium text-white mb-4" data-id="element-12978" data-mp="3kb0wognhk68y">
          Swipe through use cases
        </h2>
        <p className="text-neutral-400" data-id="element-12979" data-mp="3p8sqxhbls4gb">
          Click the top card to see the next example.
        </p>
      </div>

      <div className="relative w-full max-w-md h-[400px]" data-id="element-12980" data-mp="3u9gfzxg9ub7w">
        <AnimatePresence mode="popLayout" data-id="element-12981" data-mp="25nz91p05azo">
          {cards.map((uc, index) => {
          const isTop = index === 0;
          const offset = index * 12;
          const scale = 1 - index * 0.05;
          const zIndex = cards.length - index;
          if (index > 2) return null; // Only show top 3
          return <motion.div key={uc.id} layout initial={{
            opacity: 0,
            y: 50
          }} animate={{
            opacity: 1 - index * 0.2,
            y: offset,
            scale,
            zIndex
          }} exit={{
            opacity: 0,
            x: -200,
            rotate: -10
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 25
          }} onClick={isTop ? handleNext : undefined} className={`absolute top-0 left-0 w-full bg-white rounded-3xl p-8 shadow-2xl border border-neutral-200 ${isTop ? 'cursor-pointer hover:-translate-y-1 transition-transform' : ''}`} style={{
            transformOrigin: 'top center'
          }} data-id="element-12982" data-mp="376mzystz6okj" data-mpi={index}>
                <div className="flex items-center gap-4 mb-6" data-id="element-12983" data-mp="3uybxqopkg58v">
                  <ToolIcon slug={uc.tool.slug} light={uc.tool.light} size="lg" data-id="element-12984" data-mp="cr4m0eabgg4h" />
                  <div data-id="element-12985" data-mp="cp1q5dmhfsru">
                    <span className="text-xs font-bold text-[#EA580C] uppercase tracking-wider" data-id="element-12986" data-mp="7stbs2g403lq">
                      {uc.category}
                    </span>
                    <h3 className="text-xl font-bold text-[#2B211A]" data-id="element-12987" data-mp="rc0aypqu3oxl">
                      {uc.title}
                    </h3>
                  </div>
                </div>

                <div className="flex items-start gap-3 pt-4 border-t border-neutral-100" data-id="element-12988" data-mp="icn3g2m9tafi">
                  <div className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-[#EA580C] text-white shrink-0" data-id="element-12989" data-mp="mdp2ojg6tmhh">
                    <Check size={12} strokeWidth={3} data-id="element-12990" data-mp="8lilqv57nbgz" />
                  </div>
                  <span className="text-sm font-medium text-[#2B211A] leading-snug" data-id="element-12991" data-mp="dfwmld2n6at1">
                    {uc.result}
                  </span>
                </div>
              </motion.div>;
        })}
        </AnimatePresence>
      </div>
    </div>;
}
// --- MAIN PAGE ---
export function UseCasesConcepts() {
  return <div className="min-h-screen bg-white pt-32 pb-24" data-id="element-12992" data-mp="3lux3rc0cfz1z">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-12993" data-mp="3qthtghhyz0c6">
        <div className="mb-16" data-id="element-12994" data-mp="3qv1bodn6rlgl">
          <h1 className="text-4xl md:text-5xl font-title font-medium text-[#2B211A] mb-4" data-id="element-12995" data-mp="a6dh34lulqse">
            Use Case Concepts
          </h1>
          <p className="text-lg text-neutral-500 max-w-2xl" data-id="element-12996" data-mp="57aa3emb5ahs">
            Five interactive ways to display example use cases on a dedicated
            page. Review the interactions below and let me know which direction
            feels right.
          </p>
        </div>

        <div className="space-y-32" data-id="element-12997" data-mp="3ncna91c6ejur">
          <section data-id="element-12998" data-mp="3ndootfsehoh8">
            <div className="mb-6" data-id="element-12999" data-mp="3ne6q4qtx6sw0">
              <h2 className="text-2xl font-bold text-[#2B211A]" data-id="element-13000" data-mp="3ildmk6a6tqli">
                1. Sticker Wall (Pinboard)
              </h2>
              <p className="text-neutral-500" data-id="element-13001" data-mp="3dn3rdvogqpzw">
                Dense, free-floating cards. Click to expand the result.
              </p>
            </div>
            <ConceptStickerWall data-id="element-13002" data-mp="34lmcggmskucs" />
          </section>

          <section data-id="element-13003" data-mp="34l2w34csie0v">
            <div className="mb-6" data-id="element-13004" data-mp="34np22zk18lgz">
              <h2 className="text-2xl font-bold text-[#2B211A]" data-id="element-13005" data-mp="2zmj86yemys5v">
                2. Filterable Card Grid
              </h2>
              <p className="text-neutral-500" data-id="element-13006" data-mp="1f3yzfjc0cwz0">
                Clean, structured index. The most scalable option for many use
                cases.
              </p>
            </div>
            <ConceptCardGrid data-id="element-13007" data-mp="1o6hzjvoesvhk" />
          </section>

          <section data-id="element-13008" data-mp="1o3m3kcw4ggvr">
            <div className="mb-6" data-id="element-13009" data-mp="1t49qamdbibk6">
              <h2 className="text-2xl font-bold text-[#2B211A]" data-id="element-13010" data-mp="1t38gel81fv41">
                3. Industry Switcher
              </h2>
              <p className="text-neutral-500" data-id="element-13011" data-mp="wg3qccrcc7c8">
                Personalized entry point keyed to onboarding. "What can Giga do
                for me?"
              </p>
            </div>
            <ConceptIndustrySwitcher data-id="element-13012" data-mp="15de4q6zggcld" />
          </section>

          <section data-id="element-13013" data-mp="1a678osn3ngwe">
            <div className="mb-6" data-id="element-13014" data-mp="1a6pbk849otda">
              <h2 className="text-2xl font-bold text-[#2B211A]" data-id="element-13015" data-mp="1a7qoki4ufpxn">
                4. Animated Feed
              </h2>
              <p className="text-neutral-500" data-id="element-13016" data-mp="hpd0ha9b7ibr">
                Simulated live activity stream showing Giga working in
                real-time.
              </p>
            </div>
            <ConceptAnimatedFeed data-id="element-13017" data-mp="3lbdpjg3suzcv" />
          </section>

          <section data-id="element-13018" data-mp="3pa4oztvqmt35">
            <div className="mb-6" data-id="element-13019" data-mp="3p9l9skxh2ist">
              <h2 className="text-2xl font-bold text-[#2B211A]" data-id="element-13020" data-mp="3pcqwxumjjalw">
                5. Stacked Card Shuffle
              </h2>
              <p className="text-neutral-500" data-id="element-13021" data-mp="347s34p7ki4k">
                Playful, focused, Tinder-style stack. Great for mobile.
              </p>
            </div>
            <ConceptStackedShuffle data-id="element-13022" data-mp="31n4xd629atdd" />
          </section>
        </div>
      </div>
    </div>;
}